import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Users, Plus, Edit, Trash, Key, Shield, AlertCircle } from 'lucide-react';
import apiClient from '../services/api';
import { useToast } from '../hooks/use-toast';

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [setupMode, setSetupMode] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('landlord_token');
      const response = await apiClient.get('/api/admin/users', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUsers(response.data);
      
      // Check if we're in setup mode (no users exist)
      if (response.data.length === 0) {
        setSetupMode(true);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      if (error.response?.status === 403) {
        toast({
          title: "Access Denied",
          description: "You don't have admin permissions",
          variant: "destructive"
        });
      } else if (error.response?.status === 401) {
        // If no auth and error, we're in setup mode
        setSetupMode(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const CreateUserModal = ({ onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
      email: '',
      name: '',
      phone: '',
      password: '',
      is_admin: setupMode ? true : false,
      must_change_password: true
    });
    const [creating, setCreating] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setCreating(true);

      try {
        const token = localStorage.getItem('landlord_token');
        const response = await apiClient.post('/api/admin/users', formData, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        toast({
          title: "Success!",
          description: `User ${formData.name} created successfully`
        });

        if (response.data.temporary_password) {
          // Show password to admin
          alert(`User created!\n\nEmail: ${formData.email}\nTemporary Password: ${response.data.temporary_password}\n\nPlease save these credentials and send them to the user.`);
        }

        onSuccess();
        onClose();
      } catch (error) {
        toast({
          title: "Error",
          description: error.response?.data?.detail || "Failed to create user",
          variant: "destructive"
        });
      } finally {
        setCreating(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Create New User</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="password">Temporary Password *</Label>
                <Input
                  id="password"
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                  placeholder="Min 8 characters"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_admin"
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="is_admin">Admin User (can manage other users)</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="must_change"
                  checked={formData.must_change_password}
                  onChange={(e) => setFormData({ ...formData, must_change_password: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="must_change">Force password change on first login</Label>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1" disabled={creating}>
                  {creating ? 'Creating...' : 'Create User'}
                </Button>
                <Button type="button" variant="outline" onClick={onClose} disabled={creating}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  };

  const ResetPasswordModal = ({ user, onClose, onSuccess }) => {
    const [newPassword, setNewPassword] = useState('');
    const [mustChange, setMustChange] = useState(true);
    const [resetting, setResetting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setResetting(true);

      try {
        const token = localStorage.getItem('landlord_token');
        await apiClient.post(
          `/api/admin/users/${user.id}/reset-password`,
          {
            new_password: newPassword,
            must_change_password: mustChange
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        toast({
          title: "Success!",
          description: `Password reset for ${user.name}`
        });

        alert(`Password Reset!\n\nUser: ${user.name}\nEmail: ${user.email}\nNew Password: ${newPassword}\n\nPlease send these credentials to the user.`);

        onSuccess();
        onClose();
      } catch (error) {
        toast({
          title: "Error",
          description: error.response?.data?.detail || "Failed to reset password",
          variant: "destructive"
        });
      } finally {
        setResetting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Reset Password</CardTitle>
            <p className="text-sm text-gray-600">User: {user.name} ({user.email})</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="new_password">New Password *</Label>
                <Input
                  id="new_password"
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="Min 8 characters"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="must_change_reset"
                  checked={mustChange}
                  onChange={(e) => setMustChange(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="must_change_reset">Force password change on login</Label>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1" disabled={resetting}>
                  {resetting ? 'Resetting...' : 'Reset Password'}
                </Button>
                <Button type="button" variant="outline" onClick={onClose} disabled={resetting}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name}? This cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('landlord_token');
      await apiClient.delete(`/api/admin/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Success!",
        description: `User ${user.name} deleted`
      });

      loadUsers();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete user",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading admin panel...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {setupMode && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Initial Setup Mode</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>No admin users found. Please create your first admin user to secure this panel.</p>
                <p className="mt-1"><strong>Important:</strong> Make sure to check the "Admin User" checkbox when creating your account.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-8 w-8 text-blue-600" />
            Admin Panel
          </h1>
          <p className="text-gray-600 mt-1">Manage user accounts</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          {setupMode ? 'Create Admin User' : 'Create User'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            All Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Phone</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Created</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{user.name}</td>
                    <td className="px-4 py-3 text-sm">{user.email}</td>
                    <td className="px-4 py-3 text-sm">{user.phone || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedUser(user)}
                          title="Reset Password"
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteUser(user)}
                          className="text-red-600 hover:bg-red-50"
                          title="Delete User"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={loadUsers}
        />
      )}

      {selectedUser && (
        <ResetPasswordModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSuccess={loadUsers}
        />
      )}
    </div>
  );
};

export default Admin;
